/** PR2 draft earnings, free of statutory-tax assumptions and side effects.
 * The five hour buckets are mutually exclusive in PayrollTab. Approved OT is
 * NOT also paid as Sunday or holiday in this provisional model.
 * Only run after secure salary unlock; never persist outputs to browser cache.
 */
export const DEFAULT_EARNING_RULES = Object.freeze({
  overtimeMultiplier: 1.5,
  sundayMultiplier: 1.5,
  publicHolidayMultiplier: 2,
  paidLeaveMultiplier: 1,
});

export const LINE_ITEM_CATEGORIES = Object.freeze({
  earning: [
    { value: 'allowance', label: 'Allowance' },
    { value: 'bonus', label: 'Bonus' },
    { value: 'reimbursement', label: 'Reimbursement (tax treatment pending)' },
    { value: 'other-earning', label: 'Other earning' },
  ],
  deduction: [
    { value: 'transport', label: 'Transport' },
    { value: 'stock-loss', label: 'Stock loss (legal approval required)' },
    { value: 'salary-advance', label: 'Salary advance repayment' },
    { value: 'other-deduction', label: 'Other authorised deduction' },
  ],
});

const cents = value => Math.round((Number(value) + Number.EPSILON) * 100) / 100;
const hours = value => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

export function calculateDraftEarnings(row, salary, referenceHours, earningRules, items = [], legacyAdjustment = 0, payBasis = 'reference-hours') {
  if (payBasis !== 'reference-hours') return { ready: false, reason: 'Hourly pay basis needs a separately configured hourly rate' };
  const salaryAmount = Number(salary);
  const denominator = Number(referenceHours);
  if (salary === null || salary === undefined || !Number.isFinite(salaryAmount) || salaryAmount < 0 || !Number.isFinite(denominator) || denominator <= 0) {
    return { ready: false, reason: 'Salary or reference hours missing' };
  }
  const rules = { ...DEFAULT_EARNING_RULES, ...(earningRules || {}) };
  if (Object.keys(DEFAULT_EARNING_RULES).some(key => !Number.isFinite(Number(rules[key])) || Number(rules[key]) < 0)) {
    return { ready: false, reason: 'Invalid earning multiplier configuration' };
  }
  // Keep full precision in the derived hourly rate; round paid categories to cents.
  const hourlyRate = salaryAmount / denominator;
  const ordinary = cents(hours(row.monSatHrs) * hourlyRate);
  const sunday = cents(hours(row.sundayHours) * hourlyRate * Number(rules.sundayMultiplier));
  const holiday = cents(hours(row.publicHours) * hourlyRate * Number(rules.publicHolidayMultiplier));
  const overtime = cents(hours(row.overtimeHrs) * hourlyRate * Number(rules.overtimeMultiplier));
  const leave = cents(hours(row.paidLeaveHrs) * hourlyRate * Number(rules.paidLeaveMultiplier));
  const earningsItems = cents((items || []).filter(i => i.kind === 'earning').reduce((sum,i) => sum + (Number(i.amount) || 0),0));
  const deductions = cents((items || []).filter(i => i.kind === 'deduction').reduce((sum,i) => sum + (Number(i.amount) || 0),0));
  const grossBeforeStatutory = cents(ordinary + sunday + holiday + overtime + leave + earningsItems);
  return {
    ready: true, hourlyRate, ordinary, sunday, holiday, overtime, leave,
    earningsItems, deductions, grossBeforeStatutory,
    afterNonStatutoryDeductions: cents(grossBeforeStatutory - deductions),
    legacyAdjustment: cents(Number(legacyAdjustment) || 0),
    hasUnclassifiedAdjustment: Math.abs(Number(legacyAdjustment) || 0) > 0.0001,
  };
}
